import React, { useEffect, useRef, useState } from 'react';
import { Rabbit, Turtle } from 'lucide-react';
import { Slider } from '@/components/ui/slider';
import { cn } from '@/lib/utils';

/** 0 = completely lost, 100 = following along. Higher is better. Shared by the student slider and the professor view. */
export function confusionLabel(level: number): string {
  if (level < 20) return 'Completely lost';
  if (level < 40) return 'Confused';
  if (level < 60) return 'A bit lost';
  if (level < 80) return 'Mostly with you';
  return 'Following along';
}

/** Red at 0 through amber to green at 100. */
export function confusionColor(level: number): string {
  const hue = (level / 100) * 142;
  return `hsl(${hue.toFixed(0)} 70% 45%)`;
}

export const CONFUSION_GRADIENT =
  'linear-gradient(90deg, hsl(0 70% 50%) 0%, hsl(45 90% 50%) 50%, hsl(142 70% 45%) 100%)';

/** A pace request is a momentary ask, not a state, so it expires. Mirrors PACE_TTL_SECONDS on the server. */
export type PaceDirection = 'faster' | 'slower' | null;
export const PACE_TTL_MS = 90_000;

interface ConfusionMeterProps {
  value: number | null;
  onChange: (level: number) => void;
  pace: PaceDirection;
  onPaceChange: (direction: PaceDirection) => void;
  disabled?: boolean;
  className?: string;
}

const SEND_DEBOUNCE_MS = 350;

function PaceButton({
  direction,
  active,
  disabled,
  onClick,
}: {
  direction: Exclude<PaceDirection, null>;
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  const Icon = direction === 'slower' ? Turtle : Rabbit;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:pointer-events-none disabled:opacity-50',
        active
          ? 'border-transparent bg-foreground text-background'
          : 'bg-background text-muted-foreground hover:bg-muted hover:text-foreground'
      )}
    >
      <Icon className="h-4 w-4" aria-hidden />
      {direction === 'slower' ? 'Slower' : 'Faster'}
    </button>
  );
}

export function ConfusionMeter({
  value,
  onChange,
  pace,
  onPaceChange,
  disabled,
  className,
}: ConfusionMeterProps) {
  const [local, setLocal] = useState<number>(value ?? 100);
  const [touched, setTouched] = useState<boolean>(value != null);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (value != null) {
      setLocal(value);
      setTouched(true);
    }
  }, [value]);

  useEffect(() => {
    return () => {
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
    };
  }, []);

  const handleChange = (vals: number[]) => {
    const next = vals[0] ?? 0;
    setLocal(next);
    setTouched(true);
    if (timerRef.current != null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      onChange(next);
    }, SEND_DEBOUNCE_MS);
  };

  const color = confusionColor(local);

  return (
    <section
      className={cn('rounded-2xl border bg-card p-5 shadow-soft sm:p-6', className)}
      aria-labelledby="confusion-meter-title"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 id="confusion-meter-title" className="text-base">
            How are you doing right now?
          </h2>
        </div>
        <span
          className="shrink-0 rounded-full px-3 py-1 text-xs font-semibold text-white transition-colors"
          style={{ backgroundColor: touched ? color : undefined }}
          data-untouched={!touched || undefined}
        >
          {touched ? confusionLabel(local) : 'Not set'}
        </span>
      </div>

      <div className="mt-6">
        <Slider
          value={[local]}
          min={0}
          max={100}
          step={1}
          disabled={disabled}
          onValueChange={handleChange}
          aria-label="How well you are following, from lost to following along"
          className="[&_[data-orientation=horizontal]]:h-3 [&_[role=slider]]:h-6 [&_[role=slider]]:w-6 [&_[role=slider]]:border-2 [&_[role=slider]]:border-white [&_[role=slider]]:shadow-card [&_[role=slider]]:transition-colors"
          trackStyle={{ backgroundImage: CONFUSION_GRADIENT }}
          thumbStyle={{ backgroundColor: color }}
        />
        <div className="mt-2.5 flex justify-between text-xs font-medium text-muted-foreground">
          <span>Lost</span>
          <span>Following along</span>
        </div>
      </div>

      <div
        className="mt-6 border-t pt-5"
        role="group"
        aria-label="Ask the professor to change pace"
      >
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
          <p className="text-sm text-muted-foreground">Want a different pace?</p>
          <div className="flex gap-2">
            {(['slower', 'faster'] as const).map((direction) => (
              <PaceButton
                key={direction}
                direction={direction}
                active={pace === direction}
                disabled={disabled}
                // Tapping the active button withdraws the request.
                onClick={() => onPaceChange(pace === direction ? null : direction)}
              />
            ))}
          </div>
        </div>
        {pace && (
          <p className="mt-2.5 text-xs text-muted-foreground">
            Sent. This clears itself after a minute or so, so tap again if it still applies.
          </p>
        )}
      </div>
    </section>
  );
}
